import type { ContactRole, RepKind } from '@/api/base-types';
import { api } from '@/api/client';
import type { MenuItem } from '@/components/kit/ActionMenu';
import { useUndoable } from './useUndoable';

interface MembershipSnapshot {
  teamId: string;
  clientId: string;
  joinedOn: string;
  leftOn: string | null;
}

/**
 * Действия в строках связей (ТЗ M2 §8, memMenu / scMenu / repMenu
 * в макете): выполняются сразу, «Отменить» — обратная операция.
 */
export function useLinkActions() {
  const run = useUndoable();

  /** Строка состава: действующий период — выбыл / убрать; выбывший — вернуть. */
  const membership = (m: { id: string; teamId: string; clientId: string; leftOn: string | null; name: string }): MenuItem[] =>
    m.leftOn
      ? [
          {
            label: 'Вернуть в состав',
            description: 'новый период с сегодняшнего дня',
            onClick: () =>
              run(
                () => api.post<{ id: string; feedEntryIds: string[] }>(`/teams/${m.teamId}/members/${m.clientId}/return`),
                `${m.name} снова в составе`,
                (r) => api.delete(`/memberships/${r.id}`, { undoFeedIds: r.feedEntryIds }),
              ),
          },
        ]
      : [
          {
            label: 'Отметить выбывшим',
            description: 'история выступлений сохранится',
            onClick: () =>
              run(
                () => api.post<{ feedEntryIds: string[] }>(`/memberships/${m.id}/leave`),
                `${m.name} — выбыл(а) из состава`,
                (r) => api.post(`/memberships/${m.id}/reopen`, { undoFeedIds: r.feedEntryIds }),
              ),
          },
          {
            label: 'Убрать из состава',
            description: 'если включили по ошибке — строка удалится',
            danger: true,
            onClick: () =>
              run(
                () => api.delete<{ snapshot: MembershipSnapshot; feedEntryIds: string[] }>(`/memberships/${m.id}`, {}),
                'Строка состава удалена',
                (r) => api.post('/memberships/restore', { ...r.snapshot, undoFeedIds: r.feedEntryIds }),
              ),
          },
        ];

  /** Контакт студии: сделать основным (если ещё нет), убрать. */
  const contact = (c: { id: string; isMain: boolean }): MenuItem[] => [
    ...(c.isMain
      ? []
      : [
          {
            label: 'Сделать основным контактом',
            onClick: () =>
              run(
                () => api.post<{ previousMainId: string | null; feedEntryIds: string[] }>(`/studio-contacts/${c.id}/main`, {}),
                'Основной контакт изменён',
                (r) =>
                  r.previousMainId
                    ? api.post(`/studio-contacts/${r.previousMainId}/main`, { undoFeedIds: r.feedEntryIds })
                    : Promise.resolve(),
              ),
          },
        ]),
    {
      label: 'Убрать контакт',
      danger: true,
      onClick: () =>
        run(
          () =>
            api.delete<{
              snapshot: { studioId: string; clientId: string; role: ContactRole; isMain: boolean };
              feedEntryIds: string[];
            }>(`/studio-contacts/${c.id}`, {}),
          'Контакт убран',
          (r) =>
            api.post(`/studios/${r.snapshot.studioId}/contacts`, {
              client: { clientId: r.snapshot.clientId },
              role: r.snapshot.role,
              isMain: r.snapshot.isMain,
              undoFeedIds: r.feedEntryIds,
            }),
        ),
    },
  ];

  /** Связь «представитель»: убрать — клиенты остаются. */
  const representative = (id: string): MenuItem[] => [
    {
      label: 'Убрать связь',
      description: 'клиенты останутся, исчезнет только связь',
      danger: true,
      onClick: () =>
        run(
          () =>
            api.delete<{
              snapshot: { childClientId: string; repClientId: string; kind: RepKind };
              feedEntryIds: string[];
            }>(`/representatives/${id}`, {}),
          'Связь убрана',
          (r) =>
            api.post(`/clients/${r.snapshot.childClientId}/representatives`, {
              rep: { clientId: r.snapshot.repClientId },
              kind: r.snapshot.kind,
              undoFeedIds: r.feedEntryIds,
            }),
        ),
    },
  ];

  return { membership, contact, representative };
}
